// Smart Daara - Student Course Catalog
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { enrollmentAPI, progressAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  BookOpen, Play, CheckCircle, Clock, ArrowRight, 
  BookMarked, Send, Loader2, Lock
} from 'lucide-react';
import { toast } from 'sonner';

export default function CourseCatalog() {
  const { user } = useAuth();
  const { language, getText } = useLanguage();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('enrolled');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingCourse, setRequestingCourse] = useState(null);
  const [restartingCourse, setRestartingCourse] = useState(null);

  const handleOpenCourse = async (item, e, allowRestart = false) => {
    if (e) e.stopPropagation();
    const pct = item.progress_percentage || 0;
    const courseId = item.course.id;
    if (allowRestart && pct >= 100) {
      setRestartingCourse(courseId);
      try {
        await progressAPI.restartCourse(courseId);
        toast.success(language === 'fr' ? 'Cours réinitialisé — bon recommencement !' : 'Course reset — enjoy restarting!');
      } catch (error) {
        console.error('Error restarting course:', error);
      } finally {
        setRestartingCourse(null);
      }
    } else if (pct === 0 && !item.started) {
      progressAPI.markCourseStarted(courseId).catch(() => {});
      setEnrolledCourses(prev => prev.map(c => c.course.id === courseId ? { ...c, started: true } : c));
    }
    navigate(`/student/course/${courseId}`);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load enrolled courses
      const enrolledRes = await enrollmentAPI.getMyCourses();
      setEnrolledCourses(enrolledRes.data || []);

      // Load available courses
      const availableRes = await enrollmentAPI.getAvailableCourses();
      setAvailableCourses(availableRes.data || []);

      // Load pending requests
      const pendingRes = await enrollmentAPI.getMyRequests();
      setPendingRequests(pendingRes.data || []);
    } catch (error) {
      console.error('Error loading catalog:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEnrollment = async (courseId) => {
    setRequestingCourse(courseId);
    try {
      await enrollmentAPI.requestEnrollment(courseId);
      toast.success(
        language === 'fr' 
          ? 'Demande envoyée! En attente de validation.' 
          : 'Request sent! Waiting for approval.'
      );
      // Refresh data
      await loadData();
    } catch (error) {
      console.error('Error requesting enrollment:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la demande' 
          : 'Error sending request'
      );
    } finally {
      setRequestingCourse(null);
    }
  };

  const isPending = (courseId) => {
    return availableCourses.find(c => c.course.id === courseId)?.is_pending || false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="course-catalog">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-outfit font-bold text-slate-900">
          {language === 'fr' ? 'Mes Cours' : 'My Courses'}
        </h1>
        <p className="text-slate-500">
          {language === 'fr' 
            ? 'Gérez vos inscriptions et découvrez de nouveaux cours' 
            : 'Manage your enrollments and discover new courses'}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="enrolled" className="flex items-center gap-2" data-testid="tab-enrolled">
            <BookMarked className="w-4 h-4" />
            {language === 'fr' ? 'Mes Cours' : 'My Courses'}
            {enrolledCourses.length > 0 && (
              <Badge variant="secondary" className="ml-1">{enrolledCourses.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="catalog" className="flex items-center gap-2" data-testid="tab-catalog">
            <BookOpen className="w-4 h-4" />
            {language === 'fr' ? 'Catalogue' : 'Catalog'}
            {availableCourses.length > 0 && (
              <Badge variant="outline" className="ml-1">{availableCourses.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Enrolled Courses Tab */}
        <TabsContent value="enrolled" className="mt-6">
          {enrolledCourses.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <BookMarked className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {language === 'fr' ? 'Aucun cours inscrit' : 'No enrolled courses'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {language === 'fr' 
                    ? 'Parcourez le catalogue et demandez une inscription pour commencer' 
                    : 'Browse the catalog and request enrollment to get started'}
                </p>
                <Button onClick={() => setActiveTab('catalog')}>
                  {language === 'fr' ? 'Voir le catalogue' : 'View catalog'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {enrolledCourses.map((item, index) => (
                <Card 
                  key={item.enrollment_id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleOpenCourse(item)}
                  data-testid={`enrolled-course-${index}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-emerald-700" />
                      </div>
                      {item.progress_percentage >= 100 ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'Terminé' : 'Completed'}
                        </Badge>
                      ) : (item.progress_percentage > 0 || item.started) ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <Play className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'En cours' : 'In Progress'}
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          {language === 'fr' ? 'Nouveau' : 'New'}
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">
                      {item.course.title_fr || item.course.title}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                      {item.course.description_fr || item.course.description || 
                        (language === 'fr' ? 'Aucune description' : 'No description')}
                    </p>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">
                          {item.lessons_completed || 0}/{item.total_lessons || 0} {language === 'fr' ? 'leçons' : 'lessons'}
                        </span>
                        <span className="font-medium text-emerald-700">
                          {Math.round(item.progress_percentage || 0)}%
                        </span>
                      </div>
                      <Progress value={item.progress_percentage || 0} className="h-2" />
                    </div>

                    <Button
                      className="w-full mt-4 bg-emerald-700 hover:bg-emerald-800"
                      onClick={(e) => handleOpenCourse(item, e, true)}
                      disabled={restartingCourse === item.course.id}
                      data-testid={`course-action-btn-${index}`}
                    >
                      {restartingCourse === item.course.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {language === 'fr' ? 'Réinitialisation...' : 'Resetting...'}
                        </>
                      ) : (
                        <>
                          {item.progress_percentage >= 100
                            ? (language === 'fr' ? 'Recommencer' : 'Restart')
                            : (item.progress_percentage > 0 || item.started)
                              ? (language === 'fr' ? 'Continuer' : 'Continue')
                              : (language === 'fr' ? 'Commencer' : 'Start')
                          }
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <Card className="border-0 shadow-sm mt-6">
              <CardHeader>
                <CardTitle className="text-base font-outfit flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  {language === 'fr' ? 'Demandes en attente' : 'Pending Requests'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingRequests.map((req) => (
                    <div 
                      key={req.enrollment_id}
                      className="flex items-center justify-between p-3 bg-amber-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-amber-500" />
                        <div>
                          <p className="font-medium text-slate-900">
                            {req.course.title_fr || req.course.title}
                          </p>
                          <p className="text-sm text-slate-500">
                            {language === 'fr' ? 'En attente de validation' : 'Waiting for approval'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        {language === 'fr' ? 'En attente' : 'Pending'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog" className="mt-6">
          {availableCourses.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  {language === 'fr' ? 'Aucun cours disponible' : 'No courses available'}
                </h3>
                <p className="text-slate-500">
                  {language === 'fr' 
                    ? 'Tous les cours ont été ajoutés à vos inscriptions' 
                    : 'All courses have been added to your enrollments'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableCourses.map((item, index) => (
                <Card 
                  key={item.course.id}
                  className="border-0 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`catalog-course-${index}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-600" />
                      </div>
                      {item.is_pending && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <Clock className="w-3 h-3 mr-1" />
                          {language === 'fr' ? 'En attente' : 'Pending'}
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">
                      {item.course.title_fr || item.course.title}
                    </h3>
                    
                    {item.subject && (
                      <p className="text-sm text-emerald-600 mb-2">
                        {item.subject.name_fr || item.subject.name}
                      </p>
                    )}

                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                      {item.course.description_fr || item.course.description || 
                        (language === 'fr' ? 'Aucune description' : 'No description')}
                    </p>

                    <div className="flex items-center text-sm text-slate-500 mb-4">
                      <BookOpen className="w-4 h-4 mr-1" />
                      {item.total_lessons || 0} {language === 'fr' ? 'leçons' : 'lessons'}
                    </div>

                    {item.is_pending ? (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        disabled
                      >
                        <Clock className="w-4 h-4 mr-2 text-amber-500" />
                        {language === 'fr' ? 'Demande envoyée' : 'Request sent'}
                      </Button>
                    ) : (
                      <Button 
                        className="w-full bg-emerald-700 hover:bg-emerald-800"
                        onClick={() => handleRequestEnrollment(item.course.id)}
                        disabled={requestingCourse === item.course.id}
                        data-testid={`request-enrollment-${index}`}
                      >
                        {requestingCourse === item.course.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {language === 'fr' ? 'Envoi...' : 'Sending...'}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            {language === 'fr' ? "Demander l'inscription" : 'Request Enrollment'}
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
